import { cleanFileLabel, reportFileName } from './reportFileName';
import { effectiveAsOf, ymdOf } from './ptDates';

export type ReportsTabKey = 'skus' | 'fixed-costs' | 'sales-history' | 'variant-history' | string;

/**
 * UC-121 AC-10: the variant/SKU-grain Reports tab surface's visible label
 * reads "Variants" (not "Products"). This is a UI-copy-only rename -- the
 * underlying report type value ('skus') driving data/filters/calculations
 * is unchanged (BR-INC11-8).
 *
 * Single source of truth for OrganisationReportsTab.tsx's header and
 * tab-trigger label, so tests can exercise the exact string the component
 * renders instead of a hand-copied replica.
 */
export function getReportsTabLabel(activeReportTab: ReportsTabKey): string {
  return activeReportTab === 'skus'
    ? '∑: Variants'
    : activeReportTab === 'fixed-costs'
      ? 'Fixed Costs'
      : activeReportTab === 'variant-history'
        ? 'Variant History'
        : 'Sales History';
}

/**
 * Per-item label in the completed-exports list. Same UI-copy-only rename as
 * `getReportsTabLabel`: the stored `table_name` value ('skus') is untouched.
 */
export function getReportsExportItemLabel(tableName: string | null | undefined): string {
  if (tableName === 'skus') return 'variants';
  return tableName || 'Report Export';
}

/**
 * Filename offered when downloading a completed report export (FIG-345 UC-232, FIG-333):
 * `YYYYMMDD_<clean merchant name>_<Report>[_<label>].csv`, dated to the day the data runs
 * to. A report export runs Trailing 12 months as of yesterday in Pacific time
 * (FIG-429), so the export's created_at gives that as-of day: an export made on
 * 28 Oct 2026 (PT) is dated 20261027. Pass `asOfDate` when the export ran as of
 * another day (never later than yesterday PT). `label` is the user's optional label;
 * it is cleaned again here, so a stored value is never trusted.
 */
export function getReportsExportDownloadFilename(
  displayName: string,
  tableName: string | null | undefined,
  createdAt?: string | null,
  asOfDate?: string | null,
  label?: string | null,
): string {
  const created = createdAt ? new Date(createdAt) : new Date();
  const createdOn = Number.isNaN(created.getTime()) ? new Date() : created;
  return reportFileName({
    merchantName: displayName,
    reportType: tableName,
    dataDate: effectiveAsOf(asOfDate, createdOn),
    label,
  });
}

/**
 * What the List view needs from a stored export's `custom_sql` JSON to rebuild its file name:
 * the optional label (top-level `fileLabel`, FIG-333) and the as-of date the export ran for
 * (`filters.asOfDate`, FIG-429; empty means the default, yesterday PT). The JSON is untrusted
 * (an older or other client could store anything), so anything unexpected gives no label and
 * the default date, and the label is cleaned again.
 */
export function readExportFileParts(customSql: unknown): { label: string; asOfDate: string | null } {
  let parsed: unknown = customSql;
  if (typeof customSql === 'string') {
    try {
      parsed = JSON.parse(customSql);
    } catch {
      return { label: '', asOfDate: null };
    }
  }
  if (!parsed || typeof parsed !== 'object') return { label: '', asOfDate: null };
  const record = parsed as { fileLabel?: unknown; filters?: unknown };
  const filters = record.filters && typeof record.filters === 'object' ? (record.filters as { asOfDate?: unknown }) : null;
  return { label: cleanFileLabel(record.fileLabel), asOfDate: ymdOf(filters?.asOfDate) ?? null };
}
